import React, { useState, useEffect } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";

import api from "../../services/api";
import { decodeToken, login } from "../../services/auth";

import LogoImg from "../../assets/svg/logo.svg";
import RightImg from "../../assets/svg/background-right.svg";
import MailImg from "../../assets/svg/mail-icon.svg";
import PassImg from "../../assets/svg/pass-icon.svg";
import {
  validatePassword,
  allValidationsPassed,
  PasswordValidation,
} from "../../helpers/passwordValidation";

import { EmailStatus } from "../../models/enums/EmailStatusLogin";

import PasswordFields from "../../components/PasswordFields";
import { message, notification, Tooltip } from "antd";

import {
  Container,
  LeftSide,
  RightSide,
  Form,
  FormItem,
  Input,
  Button,
  Header,
  Logo,
  RecoverPassword,
  Col,
  Row,
  ContentLinkBottom,
  RightSideBox,
  PassIcon,
  MailIcon,
  EyeIcon,
  EyeIconInvisible,
  ContentBottom,
  ContentPrimary,
  BoxExpired,
  InfoIcon,
  ButtonResendPin,
  ReloadIcon,
} from "./styles";

interface IProps extends RouteComponentProps {}

const Login: React.FC<IProps> = ({ history }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  const RESEND_INTERVAL = 60;
  const [resendTimer, setResendTimer] = useState<number>(0);

  const startResendCountdown = () => {
    setResendTimer(RESEND_INTERVAL);
    localStorage.setItem(
      "pinResendExpiry",
      String(Date.now() + RESEND_INTERVAL * 1000)
    );
  };

  useEffect(() => {
    const expiry = localStorage.getItem("pinResendExpiry");
    if (expiry) {
      const remaining = Math.ceil((parseInt(expiry) - Date.now()) / 1000);
      if (remaining > 0) {
        setResendTimer(remaining);
      } else {
        localStorage.removeItem("pinResendExpiry");
      }
    }
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          localStorage.removeItem("pinResendExpiry");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const [passwordValidations, setPasswordValidations] =
    useState<PasswordValidation>({
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSymbol: false,
      noSequence: true,
      noTBA: true,
      noTheBest: true,
      noNameParts: true,
      passwordsMatch: false,
    });

  useEffect(() => {
    const validations = validatePassword(
      newPassword,
      confirmPassword,
      userData,
      name,
      lastName
    );
    setPasswordValidations(validations);
  }, [newPassword, confirmPassword, userData, name, lastName]);

  const clearPinAndPasswords = () => {
    setPin("");
    setNewPassword("");
    setConfirmPassword("");
    setName("");
    setLastName("");
    setIsPinVerified(false);
    setUserData(null);
  };

  const clearFields = async () => {
    setEmail("");
    setPassword("");
    clearPinAndPasswords();
    setResendTimer(0);
    await form.resetFields();
    setCurrentStep(1);
  };

  const handleRecoverPassword = async () => {
    if (resendTimer > 0) {
      notification.info({
        message: "Código já enviado",
        description: `Você já solicitou um PIN recentemente. Verifique seu email ou aguarde ${resendTimer}s para solicitar um novo código.`,
      });

      clearPinAndPasswords();
      setCurrentStep(4);
      return;
    }

    if (!email) {
      notification.warning({
        message: "Aviso",
        description: "Preencha um email válido",
      });
      return;
    }

    try {
      setLoading(true);
      await api.post("/user/recover-password", {
        email,
      });

      notification.success({
        message: "Sucesso",
        description: "Código enviado para seu email",
      });
      clearPinAndPasswords();
      startResendCountdown();
      setCurrentStep(4);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Falha ao enviar código para o email",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendPin = async () => {
    const passoAnterior = localStorage.getItem("previousStep") || "4";

    try {
      setLoading(true);

      await api.post("/user/resend-pin-user", {
        email,
      });
      notification.success({
        message: "Sucesso",
        description: "Um novo código foi enviado para seu email",
      });
      setPin("");
      setCurrentStep(parseInt(passoAnterior));
      startResendCountdown();
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Falha ao reenviar o código. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async () => {
    try {
      setLoading(true);
      if (!email) {
        notification.warning({
          message: "Aviso",
          description: "Preencha um email válido",
        });
        return;
      }

      const response = await api.post("/user/verify-email", {
        email,
      });

      const { status } = response.data;

      switch (status) {
        case EmailStatus.REGULAR:
          setCurrentStep(2);
          break;
        case EmailStatus.WITHOUT_PASS:
          setCurrentStep(3);
          break;
        case EmailStatus.OUTDATED_PASS:
          notification.warning({
            message: "Senha expirada",
            description:
              "Sua senha expirou. Enviamos um PIN para o seu email para que você possa criar uma nova senha.",
          });

          if (resendTimer === 0) {
            await generatePin();
          }

          setCurrentStep(7);
          break;
        case EmailStatus.NOT_FOUND:
          notification.error({
            message: "Erro",
            description: "Email não encontrado",
          });
          break;
        default:
          notification.error({
            message: "Erro",
            description: "Erro ao verificar email",
          });
      }
    } catch (error: any) {
      notification.error({
        message: "Erro",
        description:
          error.response?.data?.message || "Falha ao verificar email",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      if (!password) {
        notification.warning({
          message: "Aviso",
          description: "Preencha sua senha",
        });
        return;
      }

      const response = await api.post("/user/login", {
        username: email,
        password,
      });

      const { access_token, modules, permissions, countries } = response.data;

      await login(
        access_token,
        JSON.stringify(modules),
        JSON.stringify(permissions),
        JSON.stringify(countries)
      );

      const userInfo = decodeToken();
      localStorage.setItem("first_access", String(userInfo.first_access));

      message.success(`Bem-vindo ${email}`);

      if (userInfo.first_access === true) {
        history.push("/first-access");
      } else {
        history.push("/");
      }
    } catch (error: any) {
      notification.error({
        message: "Erro",
        description:
          error.response?.data?.message || "Falha ao realizar o login",
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePin = async () => {
    if (resendTimer > 0) {
      notification.warning({
        message: "Aguarde",
        description: `Você já solicitou um PIN recentemente. Tente novamente em ${resendTimer}s`,
      });
      return;
    }

    setLoading(true);
    try {
      await api.post("/user/resend-pin-user", {
        email,
      });

      notification.success({
        message: "Sucesso",
        description: "PIN enviado para seu email",
      });

      setPin("");
      setIsPinVerified(false);
      startResendCountdown();
    } catch (error: any) {
      notification.error({
        message: "Erro",
        description: error.response?.data?.message || "Falha ao gerar PIN",
      });
    } finally {
      setLoading(false);
    }
  };

  const createPassword = async () => {
    try {
      setLoading(true);

      if (!allValidationsPassed(passwordValidations)) {
        notification.warning({
          message: "Aviso",
          description:
            "Sua senha não atende aos requisitos de segurança. Verifique as validações.",
        });
        setLoading(false);
        return;
      }

      if (!userData || !userData.id) {
        notification.error({
          message: "Erro",
          description:
            "Dados do usuário não encontrados. Por favor, tente verificar o PIN novamente.",
        });
        setLoading(false);
        return;
      }

      const payload = {
        name: name || userData.name || "",
        last_name: lastName || userData.last_name || "",
        email: email,
        username: email,
        password: newPassword,
        password_confirmation: confirmPassword,
        pin_code: pin,
      };

      await api.put(`/user/create-password/${userData.id}`, payload);

      notification.success({
        message: "Sucesso",
        description: "Senha criada com sucesso!",
      });

      try {
        const response = await api.post("/user/login", {
          username: email,
          password: newPassword,
        });

        const { access_token, modules, permissions, countries } = response.data;

        const user = response.data.user || {};
        const hasFirstAccess =
          user.first_access === true || userData.first_access === true;

        await login(
          access_token,
          JSON.stringify(modules),
          JSON.stringify(permissions),
          JSON.stringify(countries)
        );

        localStorage.setItem("first_access", String(hasFirstAccess));

        message.success(`Bem-vindo ${email}`);

        if (hasFirstAccess) {
          history.push("/first-access");
        } else {
          history.push("/");
        }
      } catch (loginError: any) {
        notification.warning({
          message: "Aviso",
          description:
            "Senha criada com sucesso, mas não foi possível fazer login automático. Por favor, faça login manualmente.",
        });
        setCurrentStep(1);
      }
    } catch (error: any) {
      if (error.response) {
        notification.error({
          message: "Falha ao criar senha",
          description:
            error.response.data?.message ||
            "Falha ao criar senha. Entre em contato com o suporte para mais informações.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async () => {
    try {
      setLoading(true);

      if (!allValidationsPassed(passwordValidations)) {
        notification.warning({
          message: "Aviso",
          description:
            "Sua senha não atende aos requisitos de segurança. Verifique as validações.",
        });
        setLoading(false);
        return;
      }

      await api.post("/user/update-password", {
        email,
        pin_code: pin,
        password: newPassword,
      });

      notification.success({
        message: "Sucesso",
        description: "Senha atualizada com sucesso!",
      });

      try {
        const response = await api.post("/user/login", {
          username: email,
          password: newPassword,
        });

        const { access_token, modules, permissions, countries } = response.data;

        const user = response.data.user || {};
        const hasFirstAccess =
          user.first_access === true || userData?.first_access === true;

        await login(
          access_token,
          JSON.stringify(modules),
          JSON.stringify(permissions),
          JSON.stringify(countries)
        );

        localStorage.setItem("first_access", String(hasFirstAccess));

        message.success(`Bem-vindo ${email}`);

        if (hasFirstAccess) {
          history.push("/first-access");
        } else {
          history.push("/");
        }
      } catch (loginError: any) {
        notification.warning({
          message: "Aviso",
          description:
            "Senha atualizada com sucesso, mas não foi possível fazer login automático. Por favor, faça login manualmente.",
        });
        setCurrentStep(1);
      }
    } catch (error: any) {
      notification.error({
        message: "Falha ao atualizar senha",
        description:
          error.response?.data?.message ||
          "Falha ao atualizar senha. Verifique o console para mais detalhes.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    const noSpacesValue = value.replace(/\s+/g, "");
    const lowerCaseValue = noSpacesValue.toLowerCase();
    setEmail(lowerCaseValue);
    form.setFieldsValue({ email: lowerCaseValue });
  };

  const checkPin = async () => {
    try {
      setVerifyingPin(true);

      const verificationPin = form.getFieldValue("verificationPin") || pin;

      const response = await api.post("/user/check_pin", {
        email,
        pin_code: verificationPin,
      });

      if (response.data && response.data.content) {
        setUserData(response.data.content);
      } else {
        notification.error({
          message: "Erro",
          description:
            "Erro ao verificar PIN. Não foi possivel verificar o PIN, tente novamente mais tarde.",
        });
      }

      notification.success({
        message: "Sucesso",
        description: "PIN verificado com sucesso",
      });
      setIsPinVerified(true);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "";

      if (
        errorMessage.includes("expirou") ||
        errorMessage.includes("O código de verificação fornecido expirou") ||
        error.response?.data?.code === "PIN_EXPIRED"
      ) {
        setPin("");
        form.setFieldsValue({ verificationPin: "" });

        const passoAnterior = currentStep;
        localStorage.setItem("previousStep", String(passoAnterior));

        setCurrentStep(6);
      } else {
        notification.error({
          message: "Erro",
          description: error.response?.data?.message || "PIN inválido",
        });
        setPin("");
        form.setFieldsValue({ verificationPin: "" });
      }

      return false;
    } finally {
      setVerifyingPin(false);
    }
  };

  const renderStep1 = () => (
    <ContentPrimary>
      <Header>
        <h1>Sistemas The Best Açaí</h1>
        <p>Digite seu email para começar</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={verifyEmail}>
        <Row>
          <Col sm={24} xs={24}>
            <FormItem
              label="Email"
              name="email"
              rules={[{ required: true, message: "Preencha seu email." }]}
            >
              <Input
                name="username"
                type="email"
                autoComplete="username"
                placeholder="Digite aqui seu email"
                prefix={<MailIcon src={MailImg} alt="Email" />}
                onChange={handleEmailChange}
                value={email}
                autoFocus
              />
            </FormItem>
          </Col>
        </Row>

        <Button htmlType="submit" type="primary" loading={loading}>
          Continuar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderStep2 = () => (
    <ContentPrimary>
      <Header>
        <h1>Sistemas The Best Açaí</h1>
        <p>Digite sua senha para conectar</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={handleLogin}>
        <Row>
          <Col sm={24} xs={24}>
            <FormItem
              label="Email"
              name="email"
              rules={[{ required: true, message: "Preencha seu email." }]}
            >
              <Input
                type="email"
                autoComplete="username"
                name="username"
                value={email}
                readOnly
                onChange={handleEmailChange}
                prefix={<MailIcon src={MailImg} />}
              />
            </FormItem>
          </Col>
        </Row>

        <Row>
          <Col sm={24} xs={24}>
            <FormItem
              label="Senha"
              name="password"
              rules={[{ required: true, message: "Preencha sua senha." }]}
            >
              <Input
                name="password"
                placeholder="**********"
                autoComplete="current-password"
                type={showPassword ? "text" : "password"}
                prefix={<PassIcon src={PassImg} />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                suffix={
                  <span onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeIcon /> : <EyeIconInvisible />}
                  </span>
                }
              />
            </FormItem>
          </Col>
        </Row>

        <RecoverPassword>
          <span onClick={() => setCurrentStep(5)}>Esqueceu a sua senha?</span>
        </RecoverPassword>

        <Button htmlType="submit" type="primary" loading={loading}>
          Conectar
        </Button>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          htmlType="button"
          style={{ marginTop: "10px" }}
          onClick={() => {
            clearPinAndPasswords();
            setCurrentStep(1);
          }}
        >
          Voltar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderStep3 = () => (
    <ContentPrimary>
      <Header>
        <h1>Criar senha</h1>
        <p>Digite o PIN enviado para seu email e crie uma nova senha</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={createPassword}>
        <Row gutter={[6, 0]} style={{ display: "flex", alignItems: "center" }}>
          <Col sm={16} xs={16}>
            <FormItem
              label={
                <span>
                  PIN de verificação{" "}
                  <Tooltip title="O PIN foi enviado para o seu email. Verifique sua caixa de entrada e pasta de spam.">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
              name="verificationPin"
              rules={[
                { required: true, message: "Preencha o PIN de verificação." },
              ]}
            >
              <Input
                name="verificationPin"
                placeholder="Digite o PIN (6 dígitos)"
                maxLength={6}
                value={pin}
                autoComplete="one-time-code"
                onChange={(e) => {
                  const pinValue = e.target.value;
                  if (/^\d*$/.test(pinValue)) {
                    setPin(pinValue);
                    if (isPinVerified && pinValue.length < 6) {
                      setIsPinVerified(false);
                    }

                    if (
                      pinValue.length === 6 &&
                      !isPinVerified &&
                      !verifyingPin
                    ) {
                      checkPin();
                    }
                  }
                }}
                disabled={verifyingPin}
                autoFocus
              />
            </FormItem>
          </Col>
          <Col sm={6} xs={6} style={{ marginTop: "1rem" }}>
            <Tooltip
              title={
                resendTimer > 0
                  ? `Aguarde ${resendTimer}s para solicitar um novo PIN`
                  : ""
              }
            >
              <span style={{ display: "inline-block" }}>
                <ButtonResendPin
                  icon={<ReloadIcon />}
                  onClick={(e) => generatePin()}
                  loading={loading}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0
                    ? `Reenviar PIN (${resendTimer}s)`
                    : "Reenviar PIN"}
                </ButtonResendPin>
              </span>
            </Tooltip>
          </Col>
        </Row>

        <PasswordFields
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          showNewPassword={showNewPassword}
          setShowNewPassword={setShowNewPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          passwordValidations={passwordValidations}
          loading={loading}
          isPinVerified={isPinVerified}
        />

        <Button
          htmlType="submit"
          type="primary"
          loading={loading}
          disabled={!allValidationsPassed(passwordValidations)}
        >
          Criar senha
        </Button>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          style={{ marginTop: "10px" }}
          onClick={() => {
            clearPinAndPasswords();
            setCurrentStep(1);
          }}
        >
          Voltar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderStep4 = () => (
    <ContentPrimary>
      <Header>
        <h1>Atualizar senha</h1>
        <p>Digite o PIN enviado para seu email e crie uma nova senha</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={updatePassword}>
        <Row gutter={[6, 0]} style={{ display: "flex", alignItems: "center" }}>
          <Col sm={24} xs={24}>
            <FormItem
              label="Email"
              name="email"
              rules={[{ required: true, message: "Preencha seu email." }]}
            >
              <Input
                prefix={<MailIcon src={MailImg} alt="Email" />}
                value={email}
                disabled
              />
            </FormItem>
          </Col>
          <RecoverPassword>
            <span onClick={() => clearFields()}>Não é esse E-mail?</span>
          </RecoverPassword>
          <Col sm={16} xs={16}>
            <FormItem
              label={
                <span>
                  PIN de verificação{" "}
                  <Tooltip title="O PIN foi enviado para o seu email. Verifique sua caixa de entrada e pasta de spam.">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
              name="verificationPin"
              rules={[
                { required: true, message: "Preencha o PIN de verificação." },
              ]}
            >
              <Input
                name="verificationPin"
                placeholder="Digite o PIN (6 dígitos)"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  const pinValue = e.target.value;
                  if (/^\d*$/.test(pinValue)) {
                    setPin(pinValue);
                    if (isPinVerified && pinValue.length < 6) {
                      setIsPinVerified(false);
                    }
                    if (
                      pinValue.length === 6 &&
                      !isPinVerified &&
                      !verifyingPin
                    ) {
                      checkPin();
                    }
                  }
                }}
                disabled={verifyingPin}
                autoFocus
              />
            </FormItem>
          </Col>
          <Col sm={6} xs={6} style={{ marginTop: "1rem" }}>
            <Tooltip
              title={
                resendTimer > 0
                  ? `Aguarde ${resendTimer}s para solicitar um novo PIN`
                  : ""
              }
            >
              <span style={{ display: "inline-block" }}>
                <ButtonResendPin
                  icon={<ReloadIcon />}
                  onClick={(e) => generatePin()}
                  loading={loading}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0
                    ? `Reenviar PIN (${resendTimer}s)`
                    : "Reenviar PIN"}
                </ButtonResendPin>
              </span>
            </Tooltip>
          </Col>
        </Row>

        <PasswordFields
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          showNewPassword={showNewPassword}
          setShowNewPassword={setShowNewPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          passwordValidations={passwordValidations}
          loading={loading}
          isPinVerified={isPinVerified}
        />

        <Button
          htmlType="submit"
          type="primary"
          loading={loading}
          disabled={
            !allValidationsPassed(passwordValidations) || !isPinVerified
          }
        >
          Atualizar senha
        </Button>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          style={{ marginTop: "10px" }}
          onClick={() => {
            clearPinAndPasswords();
            setCurrentStep(1);
          }}
        >
          Voltar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderStep5 = () => (
    <ContentPrimary>
      <Header>
        <h1>Recuperar senha</h1>
        <p>Enviaremos um código para seu email</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={handleRecoverPassword}>
        <Row>
          <Col sm={24} xs={24}>
            <FormItem label="Email" name="email">
              <Input
                name="email"
                value={email}
                prefix={<img src={MailImg} alt="Email" />}
                autoFocus
              />
            </FormItem>
          </Col>
        </Row>

        <Tooltip
          title={
            resendTimer > 0
              ? `Aguarde ${resendTimer}s para solicitar um novo código`
              : ""
          }
        >
          <Button htmlType="submit" type="primary" loading={loading}>
            {resendTimer > 0 ? `Recuperar (${resendTimer}s)` : "Recuperar"}
          </Button>
        </Tooltip>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          htmlType="button"
          style={{ marginTop: "10px" }}
          onClick={(e) => {
            if (e && e.preventDefault) {
              e.preventDefault();
            }
            clearPinAndPasswords();
            setCurrentStep(2);
          }}
        >
          Voltar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderStep6 = () => {
    return (
      <ContentPrimary>
        <Header>
          <h1>Código de verificação expirado</h1>
          <p>
            O código de verificação que você inseriu expirou por motivos de
            segurança.
          </p>
        </Header>

        <BoxExpired>
          <p>
            <strong>Por que isso aconteceu?</strong>
          </p>
          <p>
            Os códigos de verificação têm um tempo de validade limitado por
            questões de segurança. É necessário solicitar um novo código para
            prosseguir com a operação.
          </p>
          <p>
            <strong>O que fazer agora?</strong>
          </p>
          <p>
            Clique no botão abaixo para receber um novo código de verificação no
            seu email.{" "}
            <Tooltip title="O PIN será enviado para o seu email. Verifique sua caixa de entrada e pasta de spam.">
              <InfoIcon style={{ color: "#1890ff", marginLeft: "5px" }} />
            </Tooltip>
          </p>
        </BoxExpired>

        <Tooltip
          title={
            resendTimer > 0
              ? `Aguarde ${resendTimer}s para solicitar um novo PIN`
              : ""
          }
        >
          <Button
            type="primary"
            loading={loading}
            onClick={handleResendPin}
            disabled={resendTimer > 0}
          >
            {resendTimer > 0
              ? `Solicitar novo código (${resendTimer}s)`
              : "Solicitar novo código"}
          </Button>
        </Tooltip>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          style={{ marginTop: "10px" }}
          onClick={() => {
            clearPinAndPasswords();
            setCurrentStep(1);
          }}
        >
          Voltar ao início
        </Button>
      </ContentPrimary>
    );
  };

  const renderStep7 = () => (
    <ContentPrimary>
      <Header>
        <div className="header-title">
          <h1>Senha expirada</h1>
          <Tooltip title="Por segurança, sua senha expira a cada 90 dias. Crie uma nova senha para continuar.">
            <InfoIcon fontSize="1.2rem" />
          </Tooltip>
        </div>
        <p>Digite o PIN enviado para seu email e crie uma nova senha.</p>
      </Header>

      <Form layout="vertical" form={form} onFinish={updatePassword}>
        <Row gutter={[6, 0]} style={{ display: "flex", alignItems: "center" }}>
          <Col sm={16} xs={16}>
            <FormItem
              label={
                <span>
                  PIN de verificação{" "}
                  <Tooltip title="O PIN foi enviado para o seu email. Verifique sua caixa de entrada e pasta de spam.">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
              name="verificationPin"
              rules={[
                { required: true, message: "Preencha o PIN de verificação." },
              ]}
            >
              <Input
                name="verificationPin"
                placeholder="Digite o PIN (6 dígitos)"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  const pinValue = e.target.value;
                  if (/^\d*$/.test(pinValue)) {
                    setPin(pinValue);
                    if (isPinVerified && pinValue.length < 6) {
                      setIsPinVerified(false);
                    }
                    if (
                      pinValue.length === 6 &&
                      !isPinVerified &&
                      !verifyingPin
                    ) {
                      checkPin();
                    }
                  }
                }}
                disabled={verifyingPin}
                autoFocus
              />
            </FormItem>
          </Col>
          <Col sm={6} xs={6} style={{ marginTop: "1rem" }}>
            <Tooltip
              title={
                resendTimer > 0
                  ? `Aguarde ${resendTimer}s para solicitar um novo PIN`
                  : ""
              }
            >
              <span style={{ display: "inline-block" }}>
                <ButtonResendPin
                  icon={<ReloadIcon />}
                  onClick={(e) => generatePin()}
                  loading={loading}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0
                    ? `Reenviar PIN (${resendTimer}s)`
                    : "Reenviar PIN"}
                </ButtonResendPin>
              </span>
            </Tooltip>
          </Col>
        </Row>

        <PasswordFields
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          showNewPassword={showNewPassword}
          setShowNewPassword={setShowNewPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          passwordValidations={passwordValidations}
          loading={loading}
          isPinVerified={isPinVerified}
        />

        <Button
          htmlType="submit"
          type="primary"
          loading={loading}
          disabled={
            !allValidationsPassed(passwordValidations) || !isPinVerified
          }
        >
          Atualizar senha
        </Button>

        <Button
          background="var(--gray-150)"
          backgroundHover="var(--gray-300)"
          style={{ marginTop: "10px" }}
          onClick={() => {
            clearPinAndPasswords();
            setCurrentStep(1);
          }}
        >
          Voltar
        </Button>
      </Form>
    </ContentPrimary>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      default:
        return renderStep1();
    }
  };

  return (
    <Container>
      <LeftSide>
        <Logo src={LogoImg} />
        {renderCurrentStep()}

        <ContentBottom>
          <p>
            Está com algum problema? Entre em contato conosco{" "}
            <a href="mailto:comunicathebestacai@gmail.com">
              comunicathebestacai@gmail.com
            </a>
            .
          </p>
          <ContentLinkBottom>
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://thebestacai.com.br/privacidade"
              className="privacity-term"
            >
              Aviso de Privacidade
            </a>
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://gaa.digital/thebest"
              className="privacity-term"
            >
              Canal de denúncias
            </a>
          </ContentLinkBottom>
        </ContentBottom>
      </LeftSide>

      <RightSideBox>
        <RightSide src={RightImg}>
          <h1>REINVENTE-SE</h1>
          <p>Faça o seu melhor!</p>
        </RightSide>
      </RightSideBox>
    </Container>
  );
};

export default withRouter(Login);
